import { App, Skeleton } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { kbApi, kbKeys, useKbOverview, type KbSource } from '@entities/knowledge';
import { sourceApi } from '@entities/source';
import { watchKb } from '@features/kb-watch';
import { extractErrorMessage } from '@shared/api';
import { IconCheck, plural, TopBar } from '@shared/ui/v2';
import { extOf, imagesWord, money, pagesWord, TypeBadge, when, whenAt } from './kbUi';
import { ACCEPT, UploadModal } from './UploadModal';
import './knowledge.css';

const STEP_TOTAL = 4;

function title(s: KbSource) {
  if (s.type === 'website') return s.display_name || s.name || 'Сайт';
  return s.name || s.display_name || 'Документ';
}

function subtitle(s: KbSource) {
  if (s.type === 'website') {
    const n =
      s.state === 'processing' || s.state === 'queued'
        ? (s.pages_total ?? s.site_pages)
        : (s.site_pages ?? s.pages_total ?? s.est_pages);
    return n ? `Сайт · ${n} ${pagesWord(n)}` : 'Сайт';
  }
  if (s.est_pages == null) return 'Документ';
  if (s.state === 'error' && s.est_pages === 0) return '0 страниц с текстом';
  const img = s.est_images ?? 0;
  const pptx = extOf(s.name) === 'pptx';
  return `${s.est_pages} ${pptx ? plural(s.est_pages, 'слайд', 'слайда', 'слайдов') : pagesWord(s.est_pages)}${s.process_images && img ? ` · ${img} ${imagesWord(img)}` : ''}`;
}

function Status({
  s,
  onConfirm,
  onStart,
  onDelete,
}: {
  s: KbSource;
  onConfirm: () => void;
  onStart: () => void;
  onDelete: () => void;
}) {
  if (s.state === 'ready') return <span className="kb-pill is-ok">Готов</span>;
  if (s.state === 'queued') return <span className="kb-pill">В очереди</span>;
  if (s.state === 'processing') {
    const pct = s.percent ?? 3;
    return (
      <div className="kb-proc">
        <span className="kb-pill is-blue">Обработка · {pct}%</span>
        <div className="kb-bar">
          <div style={{ width: `${pct}%` }} />
        </div>
        <div className="v2-xs v2-muted">
          {s.type === 'website'
            ? s.pages_total
              ? `страницы ${s.pages_done ?? 0} из ${s.pages_total}`
              : 'начинаем…'
            : s.step
              ? `Шаг ${s.step} из ${STEP_TOTAL}${s.step === 3 && s.images_total ? ` · картинки ${s.images_done ?? 0} из ${s.images_total}` : ''}`
              : 'начинаем…'}
        </div>
      </div>
    );
  }
  if (s.state === 'awaiting') {
    return (
      <div className="kb-proc">
        <span className="kb-pill is-orange">
          {s.autostart ? 'Ждёт оплаты' : 'Ждёт подтверждения'}
        </span>
        {s.autostart ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Link to="/billing?topup=1" className="v2-btn is-primary kb-mini">
              Пополнить
            </Link>
            <button type="button" className="v2-btn kb-mini" onClick={onStart}>
              Запустить
            </button>
          </div>
        ) : (
          <button type="button" className="v2-btn is-primary kb-mini" onClick={onConfirm}>
            Подтвердить
          </button>
        )}
      </div>
    );
  }
  // Ошибка — коротко: значок и «Удалить». Причина — в подробностях справа.
  return (
    <div className="kb-proc is-row">
      <span className="kb-pill is-red">Ошибка</span>
      <button type="button" className="v2-btn kb-mini kb-danger" onClick={onDelete}>
        Удалить
      </button>
    </div>
  );
}

/** Причина ошибки и что с деньгами — без повтора, если причина уже про деньги. */
function errorText(s: KbSource) {
  const reason = s.error ?? 'Не удалось обработать.';
  if (/деньги/i.test(reason)) return reason;
  return `${reason} ${
    Number(s.reserved_rub) > 0
      ? `Резерв ${money(s.reserved_rub)} вернём автоматически.`
      : 'Деньги не списаны.'
  }`;
}

/** Можно удалить: всё, кроме того, что сейчас в обработке. */
const deletable = (s: KbSource) => s.state !== 'processing' && s.state !== 'queued';

function updated(s: KbSource) {
  if (s.state === 'processing' || s.state === 'queued') return 'сейчас';
  if (s.state === 'awaiting') {
    if (s.autostart) return 'запустится после оплаты';
    return s.confirm_expires_at ? `подтвердить до ${when(s.confirm_expires_at)}` : '—';
  }
  return when(s.created_at);
}

function cost(s: KbSource) {
  if (s.state === 'ready') return money(s.charged_rub);
  if (s.state === 'error') return money(0);
  if (s.state === 'processing' && Number(s.reserved_rub) > 0) return money(s.reserved_rub, true);
  return s.estimate_rub != null ? money(s.estimate_rub, true) : '—';
}

/** ETA, мин: грубо — страница ~1 с, картинка ~2 с, с поправкой на %. */
function etaMinutes(list: KbSource[]) {
  let sec = 0;
  for (const s of list) {
    const base =
      (s.est_pages ?? s.pages_total ?? 20) * (s.type === 'website' ? 3 : 1) +
      (s.process_images ? (s.est_images ?? 0) * 2 : 0);
    sec += base * (1 - (s.percent ?? 0) / 100);
  }
  return Math.max(1, Math.round(sec / 60));
}

/**
 * Экран 5 «Ассистент → База знаний» и 5в «во время обработки» (утверждены 28.10.2026).
 */
export function KnowledgePage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const { data, isLoading, error } = useKbOverview(id);
  const [selId, setSelId] = useState<string | null>(null);
  const [upload, setUpload] = useState<{
    open: boolean;
    files?: File[];
    replaceId?: string | null;
  }>({ open: false });
  const [drag, setDrag] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const fileInput = useRef<HTMLInputElement>(null);

  // С «Нового ассистента»: ?upload=1 — сразу окно загрузки, ?site=1 — подключение сайта.
  useEffect(() => {
    if (sp.get('upload') === '1') {
      setUpload({ open: true });
      sp.delete('upload');
      setSp(sp, { replace: true });
    } else if (sp.get('site') === '1') {
      navigate(`/assistants/${id}/knowledge/site`, { replace: true });
    }
  }, [sp, setSp, navigate, id]);

  const sources = useMemo(() => data?.sources ?? [], [data]);
  const sel =
    sources.find((s) => s.id === selId) ??
    sources.find((s) => s.state === 'processing') ??
    sources[0];
  const busy = sources.filter((s) => s.state === 'processing' || s.state === 'queued');
  const canDel = sources.filter(deletable);
  const picked = canDel.filter((s) => checked.has(s.id));
  const allPicked = canDel.length > 0 && picked.length === canDel.length;
  const toggle = (sid: string) =>
    setChecked((c) => {
      const n = new Set(c);
      if (n.has(sid)) n.delete(sid);
      else n.add(sid);
      return n;
    });

  // Р46: одно уведомление, когда обработана вся база, — показывает KbWatcher на
  // любой странице кабинета. Здесь только сообщаем, что идёт обработка.
  const busyKey = busy.map((s) => s.id).join(',');
  useEffect(() => {
    if (busyKey) watchKb(id, busyKey.split(','));
  }, [busyKey, id]);

  const asideRef = useRef<HTMLElement>(null);
  const pick = (sid: string) => {
    setSelId(sid);
    // На узком экране подробности стоят над таблицей — показываем их.
    requestAnimationFrame(() => asideRef.current?.scrollIntoView({ block: 'nearest' }));
  };

  const counts = {
    ready: sources.filter((s) => s.state === 'ready').length,
    processing: sources.filter((s) => s.state === 'processing').length,
    queued: sources.filter((s) => s.state === 'queued').length,
    awaiting: sources.filter((s) => s.state === 'awaiting' && !s.autostart).length,
    payment: sources.filter((s) => s.state === 'awaiting' && s.autostart).length,
    error: sources.filter((s) => s.state === 'error').length,
  };
  const verb = (k: number, one: string, many: string) => (k === 1 ? one : many);
  const n = sources.length;
  const srcWord =
    n % 10 === 1 && n % 100 !== 11
      ? 'источник'
      : [2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)
        ? 'источника'
        : 'источников';
  const hint = !n
    ? '0,5 ₽ за страницу, 1 ₽ за картинку, цену покажем до списания'
    : counts.ready === n
      ? `${n} ${srcWord} · всё готово · 0,5 ₽ за страницу, 1 ₽ за картинку, цену покажем до списания`
      : [
          `${n} ${srcWord}`,
          counts.ready && `${counts.ready} ${verb(counts.ready, 'готов', 'готовы')}`,
          counts.processing &&
            `${counts.processing} ${verb(counts.processing, 'обрабатывается', 'обрабатываются')}`,
          counts.queued && `${counts.queued} в очереди`,
          counts.awaiting &&
            `${counts.awaiting} ${verb(counts.awaiting, 'ждёт', 'ждут')} подтверждения`,
          counts.payment && `${counts.payment} ${verb(counts.payment, 'ждёт', 'ждут')} оплаты`,
          counts.error && `${counts.error} с ошибкой`,
        ]
          .filter(Boolean)
          .join(' · ');

  const confirmOne = async (s: KbSource) => {
    try {
      await kbApi.confirmBatch(id, [s.id], s.process_images);
      watchKb(id, [s.id]);
      qc.invalidateQueries({ queryKey: kbKeys.overview(id) });
      qc.invalidateQueries({ queryKey: kbKeys.funds });
    } catch (e) {
      const st = (e as { response?: { status?: number } }).response?.status;
      if (st === 402) {
        message.warning(extractErrorMessage(e));
        qc.invalidateQueries({ queryKey: kbKeys.overview(id) });
      } else message.error(extractErrorMessage(e));
    }
  };

  const removeOne = (s: KbSource) =>
    s.state === 'awaiting' ? kbApi.removePending(s.id) : sourceApi.delete(s.id);

  const del = (list: KbSource[]) => {
    if (!list.length) return;
    const onlyErrors = list.every((s) => s.state === 'error' || s.state === 'awaiting');
    modal.confirm({
      title:
        list.length === 1
          ? `Удалить «${title(list[0]!)}»?`
          : `Удалить ${list.length} ${plural(list.length, 'источник', 'источника', 'источников')}?`,
      content: onlyErrors
        ? 'Деньги за них не списывались.'
        : 'Ассистент перестанет отвечать по этим источникам. Деньги за загрузку не возвращаются.',
      okText: 'Удалить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        const res = await Promise.allSettled(list.map(removeOne));
        const failed = res.filter((x) => x.status === 'rejected') as PromiseRejectedResult[];
        if (failed.length)
          message.error(
            `Не удалось удалить ${failed.length} из ${list.length}: ${extractErrorMessage(failed[0]!.reason)}`,
          );
        setChecked(new Set());
        setSelId(null);
        qc.invalidateQueries({ queryKey: kbKeys.overview(id) });
        qc.invalidateQueries({ queryKey: ['cabinet'] });
      },
    });
  };

  const replace = (s: KbSource) => setUpload({ open: true, replaceId: s.id });

  // «Ждёт оплаты» → «Запустить»: если деньги уже есть (пополнили не через ЮKassa
  // или автозапуск не сработал) — стартуем сразу.
  const startWaiting = async () => {
    try {
      const r = await kbApi.startWaiting(id);
      if (r.started) message.success('Запустили обработку');
      qc.invalidateQueries({ queryKey: kbKeys.overview(id) });
      qc.invalidateQueries({ queryKey: kbKeys.funds });
    } catch (e) {
      message.warning(extractErrorMessage(e));
    }
  };

  const busyTitle = (() => {
    const site = busy.some((s) => s.type === 'website');
    const docs = busy.filter((s) => s.type !== 'website').length;
    if (site && docs) return docs === 1 ? 'сайт и документ' : 'сайт и документы';
    if (site) return 'сайт';
    return docs === 1 ? 'документ' : 'документы';
  })();

  return (
    <>
      <TopBar
        section
        title="База знаний"
        hint={hint}
        actions={
          <>
            <button
              type="button"
              className="v2-btn is-primary"
              onClick={() => setUpload({ open: true })}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 16V4M7 9l5-5 5 5M4 20h16" />
              </svg>
              Загрузить файлы
            </button>
            <Link to={`/assistants/${id}/knowledge/site`} className="v2-btn">
              Подключить сайт
            </Link>
          </>
        }
      />
      <div className="v2-content is-section">
        {isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
        {error && (
          <div className="v2-card v2-card-pad">
            Не удалось загрузить базу знаний: {extractErrorMessage(error)}
          </div>
        )}
        {busy.length > 0 && (
          <div className="v2-card kb-banner" role="status">
            <span className="kb-spinner" aria-hidden="true" />
            <span>
              <b>
                Обрабатываем {busyTitle} · осталось около {etaMinutes(busy)} мин.
              </b>{' '}
              Можно закрыть страницу — пришлём уведомление. Ассистент уже отвечает по готовым
              источникам.
            </span>
          </div>
        )}
        {data && (
          <div className="kb-grid is-kb">
            <section aria-label="Источники" className="v2-card kb-table-card">
              {picked.length > 0 && (
                <div className="kb-selbar" role="region" aria-label="Выбранные источники">
                  <span>
                    Выбрано {picked.length} из {canDel.length}
                  </span>
                  <button
                    type="button"
                    className="v2-btn kb-mini kb-danger"
                    onClick={() => del(picked)}
                  >
                    Удалить выбранные
                  </button>
                  <button
                    type="button"
                    className="v2-btn kb-mini"
                    onClick={() => setChecked(new Set())}
                  >
                    Снять выделение
                  </button>
                </div>
              )}
              {sources.length > 0 && (
                <div className="v2-table-wrap">
                  <table className="v2-table kb-table">
                    <thead>
                      <tr>
                        <th scope="col" className="kb-check">
                          <input
                            type="checkbox"
                            aria-label="Выбрать все, кроме тех, что в обработке"
                            title="Выбрать все"
                            checked={allPicked}
                            ref={(el) => {
                              if (el) el.indeterminate = picked.length > 0 && !allPicked;
                            }}
                            disabled={!canDel.length}
                            onChange={() =>
                              setChecked(allPicked ? new Set() : new Set(canDel.map((s) => s.id)))
                            }
                          />
                        </th>
                        <th scope="col">Источник</th>
                        <th scope="col">Статус</th>
                        <th scope="col">Обновлён</th>
                        <th scope="col" style={{ textAlign: 'right' }}>
                          Стоимость
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sources.map((s) => (
                        <tr
                          key={s.id}
                          className={s.id === sel?.id ? 'is-sel' : undefined}
                          onClick={() => pick(s.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="kb-check" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              aria-label={`Выбрать «${title(s)}»`}
                              checked={checked.has(s.id) && deletable(s)}
                              disabled={!deletable(s)}
                              onChange={() => toggle(s.id)}
                            />
                          </td>
                          <td className="v2-wrap">
                            <div className="kb-src">
                              <TypeBadge name={s.name} site={s.type === 'website'} />
                              <div style={{ minWidth: 0 }}>
                                <div className="kb-src-name">{title(s)}</div>
                                <div className="v2-xs v2-muted">{subtitle(s)}</div>
                              </div>
                            </div>
                          </td>
                          <td
                            className="v2-wrap"
                            style={{ minWidth: 200 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Status
                              s={s}
                              onConfirm={() => confirmOne(s)}
                              onStart={startWaiting}
                              onDelete={() => del([s])}
                            />
                          </td>
                          <td className="v2-wrap" style={{ minWidth: 110 }}>
                            {updated(s)}
                          </td>
                          <td
                            style={{
                              textAlign: 'right',
                              color: s.state === 'ready' ? undefined : '#6B7280',
                            }}
                          >
                            {cost(s)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {sources.length === 0 && (
                <div className="kb-empty">
                  <div style={{ fontWeight: 600, fontSize: 15 }}>Пока пусто</div>
                  <div className="v2-muted v2-small">
                    Загрузите инструкции, прайсы, договоры или подключите сайт — ассистент будет
                    отвечать по ним.
                  </div>
                </div>
              )}
              <div
                className={drag ? 'kb-drop is-drag' : 'kb-drop'}
                role="button"
                tabIndex={0}
                onClick={() => fileInput.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDrag(true);
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDrag(false);
                  setUpload({ open: true, files: Array.from(e.dataTransfer.files) });
                }}
              >
                <b>Перетащите файлы сюда</b> — можно несколько сразу или ZIP-архив · PDF, Word,
                PowerPoint, Excel, TXT
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  accept={ACCEPT}
                  hidden
                  onChange={(e) => {
                    const files = Array.from(e.target.files ?? []);
                    e.target.value = '';
                    if (files.length) setUpload({ open: true, files });
                  }}
                />
              </div>
            </section>

            {sel && (
              <aside
                ref={asideRef}
                aria-label="Подробности источника"
                className={`v2-card kb-aside${selId ? ' is-picked' : ''}`}
              >
                <div className="kb-src">
                  <TypeBadge name={sel.name} site={sel.type === 'website'} />
                  <div style={{ minWidth: 0 }}>
                    <div className="kb-src-name" style={{ fontSize: 15 }}>
                      {title(sel)}
                    </div>
                    <div className="v2-xs v2-muted">
                      {sel.state === 'processing' || sel.state === 'queued'
                        ? `Запущено ${whenAt(sel.created_at)}`
                        : `Загружен ${whenAt(sel.created_at)}`}
                    </div>
                  </div>
                  {selId && (
                    <button
                      type="button"
                      className="v2-icon-btn kb-aside-x"
                      aria-label="Закрыть подробности"
                      onClick={() => setSelId(null)}
                    >
                      ×
                    </button>
                  )}
                </div>
                {sel.state === 'processing' && sel.type === 'file' && (
                  <ol className="kb-steps">
                    {[
                      'Читаем файл',
                      'Делим на фрагменты',
                      'Распознаём картинки',
                      'Готовим к поиску',
                    ].map((t, i) => {
                      const n = i + 1;
                      const cur = sel.step ?? 1;
                      const skip = n === 3 && !sel.process_images;
                      return (
                        <li
                          key={t}
                          className={
                            n < cur ? 'is-done' : n === cur ? 'is-cur' : skip ? 'is-skip' : ''
                          }
                        >
                          <span className="kb-step-dot">
                            {n < cur ? <IconCheck size={10} /> : n}
                          </span>
                          {t}
                          {n === 3 && cur === 3 && sel.images_total
                            ? ` · ${sel.images_done ?? 0} из ${sel.images_total}`
                            : ''}
                          {skip ? ' — выключено' : ''}
                        </li>
                      );
                    })}
                  </ol>
                )}
                {sel.type === 'website' &&
                (sel.state === 'processing' || sel.state === 'queued') ? (
                  <div className="kb-kv-text">
                    Страницы {sel.pages_done ?? 0} из {sel.pages_total ?? '…'} · только текст, без
                    картинок
                  </div>
                ) : (
                  <div className="kb-kv">
                    <div>
                      <span>Статус</span>
                      <span className={sel.state === 'ready' ? 'kb-ok' : undefined}>
                        {sel.state === 'ready'
                          ? 'Готов, ассистент использует'
                          : sel.state === 'processing'
                            ? 'Обрабатывается'
                            : sel.state === 'queued'
                              ? 'В очереди'
                              : sel.state === 'awaiting'
                                ? sel.autostart
                                  ? 'Ждёт оплаты'
                                  : 'Ждёт подтверждения'
                                : 'Ошибка'}
                      </span>
                    </div>
                    {sel.type === 'file' && (
                      <div>
                        <span>Картинки в ответах</span>
                        <span>
                          {!sel.process_images
                            ? 'Не распознаём'
                            : data.show_images
                              ? 'Включены'
                              : 'Выключены'}
                        </span>
                      </div>
                    )}
                    <div>
                      <span>{sel.state === 'ready' ? 'Списано за загрузку' : 'Стоимость'}</span>
                      <span>{cost(sel)}</span>
                    </div>
                  </div>
                )}
                {sel.state === 'error' && (
                  <div className="v2-xs" style={{ color: '#DC2626' }}>
                    {errorText(sel)}
                  </div>
                )}
                {(sel.state === 'processing' || sel.state === 'queued') && (
                  <div className="v2-xs v2-muted">
                    {Number(sel.reserved_rub) > 0
                      ? `Зарезервировано ${money(sel.reserved_rub)} — спишем по факту.`
                      : `Спишем не больше ${money(sel.estimate_rub)} — по факту.`}
                  </div>
                )}
                <div className="kb-actions">
                  {sel.type === 'website' && (
                    <Link
                      to={`/assistants/${id}/knowledge/site/${sel.id}`}
                      className="v2-btn is-primary"
                    >
                      Открыть страницу сайта
                    </Link>
                  )}
                  {sel.state === 'ready' && sel.type !== 'website' && (
                    <Link to={`/assistants/${id}/test-chat`} className="v2-btn is-primary">
                      Спросить в тест-чате
                    </Link>
                  )}
                  {sel.type === 'file' && sel.state !== 'processing' && sel.state !== 'queued' && (
                    <button type="button" className="v2-btn" onClick={() => replace(sel)}>
                      Заменить
                    </button>
                  )}
                  {deletable(sel) && (
                    <button type="button" className="v2-btn kb-danger" onClick={() => del([sel])}>
                      Удалить
                    </button>
                  )}
                </div>
              </aside>
            )}
          </div>
        )}
      </div>

      <UploadModal
        assistantId={id}
        open={upload.open}
        initialFiles={upload.files}
        replaceId={upload.replaceId}
        onClose={() => setUpload({ open: false })}
      />
    </>
  );
}
